// §29.4 pure pricing engine. Integer-only arithmetic under the unit discipline
// declared in ./types.ts (cents / minutes / basis-points / ft_x10). No I/O,
// fully deterministic: same input + same profile → byte-identical output.
//
// Every intermediate that could be fractional is collapsed to an integer with
// `roundHalfUp` at the exact spec-defined step, and NEVER rounded a second time
// for the same quantity — this is what keeps the breakdown summing to the total
// with no drift.
import { AppError } from '../errors.js';
import { pricePrintLine } from './print.js';
// ---------------------------------------------------------------------------
// Rounding. All inputs to this engine are non-negative integers, and every
// division is by a positive power-of-ten (or 60/positive) denominator, so the
// dividend is always ≥ 0. JS `Math.round` rounds half UP for non-negative
// numbers (Math.round(0.5) === 1, Math.round(2.5) === 3), which is exactly the
// round-half-up rule §29.4 requires. We therefore delegate to Math.round rather
// than reimplementing it. (Math.round only diverges from round-half-up for
// NEGATIVE half-values — Math.round(-0.5) === 0, not -1 — which cannot occur
// here because no quantity fed to roundHalfUp is ever negative; the guard below
// enforces that assumption so a future negative input fails loudly instead of
// silently rounding the wrong way.)
function roundHalfUp(n) {
    if (n < 0) {
        throw new AppError('VALIDATION', `roundHalfUp received a negative value (${n}); pricing inputs must be non-negative`, {
            userMessage: 'Something went wrong pricing this line. Please contact support.',
        });
    }
    return Math.round(n);
}
// ---------------------------------------------------------------------------
// Invariant guards (§29.8). Violations are internal bugs, not user errors.
// ---------------------------------------------------------------------------
function assertNonNegInt(value, label) {
    if (!Number.isInteger(value) || value < 0) {
        throw new AppError('VALIDATION', `pricing invariant: ${label} must be a non-negative integer, got ${value}`, {
            userMessage: 'Something went wrong pricing this line. Please contact support.',
        });
    }
}
function assertBreakdownSums(entries, total, context) {
    const sum = entries.reduce((acc, e) => acc + e.cents, 0);
    if (sum !== total) {
        throw new AppError('VALIDATION', `pricing invariant: ${context} breakdown sums to ${sum} but line subtotal is ${total}`, {
            userMessage: 'Something went wrong pricing this line. Please contact support.',
        });
    }
}
// ---------------------------------------------------------------------------
// Shared cost helpers — one rounding per named component, at the spec step.
// ---------------------------------------------------------------------------
/** Labor cents from minutes at an hourly cents rate: minutes × rate / 60. */
function laborCentsFromMinutes(minutes, rateCentsPerHour) {
    return roundHalfUp((minutes * rateCentsPerHour) / 60);
}
/** Apply a basis-point markup: cents × (10000 + markupBp) / 10000. */
function applyMarkup(cents, markupBp) {
    return roundHalfUp((cents * (10000 + markupBp)) / 10000);
}
// ===========================================================================
// WRAP — §29.4 rules 1–5.
// ===========================================================================
/**
 * §29.4 rule 1 (labor minutes) + rule 2 (film ft_x10) + rules 3–5 (material,
 * labor, line subtotal). Operation order is exactly as specified:
 *   labor_minutes → film_ft_x10 → material_cents → labor_cents → line subtotal.
 */
export function priceWrapLine(input) {
    const finish = input.finishDefs[input.finishKey];
    // -- §29.4 rule 1: labor minutes ----------------------------------------
    // full_wrap base term:  round(base_minutes × complexity_bp × finish_labor_bp / 1e8)
    // partial coverage: the base term is REPLACED by
    //   round(Σ panel_minutes × complexity_bp / 10000)   [finish labor bp still
    //   applies multiplicatively on top — §29.4 rule 2 note].
    let coverageBaseMinutes;
    let coverageBaseFtX10;
    if (input.coverage.kind === 'full_wrap') {
        coverageBaseMinutes = roundHalfUp((input.vehicleBaseMinutes * input.complexityBp * finish.laborBp) / 1e8);
        coverageBaseFtX10 = input.vehicleBaseFtX10;
    }
    else {
        // partial_panels / chrome_delete / decals_lettering / stripes
        let panelMinutes;
        let panelFtX10;
        if (input.coverage.kind === 'partial_panels') {
            panelMinutes = input.coverage.panels.reduce((acc, p) => acc + p.minutes, 0);
            panelFtX10 = input.coverage.panels.reduce((acc, p) => acc + p.ftX10, 0);
        }
        else {
            panelMinutes = input.coverage.minutes;
            panelFtX10 = input.coverage.ftX10;
        }
        // Σ panel_minutes × complexity_bp / 10000, then finish labor bp on top.
        const complexityApplied = (panelMinutes * input.complexityBp) / 10000;
        coverageBaseMinutes = roundHalfUp((complexityApplied * finish.laborBp) / 10000);
        coverageBaseFtX10 = panelFtX10;
    }
    const modifierMinutes = input.modifiers.reduce((acc, key) => acc + input.modifierDefs[key].addedMinutes, 0);
    const modifierFtX10 = input.modifiers.reduce((acc, key) => acc + input.modifierDefs[key].addedFtX10, 0);
    const disassemblyMinutes = input.disassemblyMinutesByLevel[input.disassemblyLevel];
    const jambsMinutes = input.doorJambs ? input.jambsMinutes : 0;
    const jambsFtX10 = input.doorJambs ? input.jambsFtX10 : 0;
    const defectMinutes = input.defectPanelCount * input.defectMinutesPerPanel;
    const removalMinutes = input.removalTier === 'none' ? 0 : input.removalMinutesByTier[input.removalTier];
    const laborMinutes = coverageBaseMinutes +
        modifierMinutes +
        disassemblyMinutes +
        jambsMinutes +
        defectMinutes +
        removalMinutes;
    // -- §29.4 rule 2: film ft_x10 ------------------------------------------
    // (base_or_panel_ft + Σmodifier_ft + jambs_ft) × (10000 + waste_bp + finish_extra_waste_bp) / 10000
    const rawFtX10 = coverageBaseFtX10 + modifierFtX10 + jambsFtX10;
    const filmFtX10 = roundHalfUp((rawFtX10 * (10000 + input.wasteBp + finish.extraWasteBp)) / 10000);
    // -- §29.4 rule 3: material cents ---------------------------------------
    // film_ft_x10 × cost_per_ft_cents / 10 × finish_material_bp / 10000, then × markup.
    const materialBeforeMarkup = roundHalfUp((filmFtX10 * input.costPerFtCents * finish.materialBp) / (10 * 10000));
    const materialCostCents = applyMarkup(materialBeforeMarkup, input.markupBp);
    // -- §29.4 rule 4: labor cents ------------------------------------------
    // All labor minutes bill at the input's labor rate. (The optional
    // removalRateCentsPerHour override on ResolvedProfileVersion is resolved by
    // the caller into this single rate; billing all minutes at one rate keeps the
    // exact-sum invariant with a single labor line.)
    const laborCostCents = laborCentsFromMinutes(laborMinutes, input.laborRateCentsPerHour);
    // Design cents: design_minutes × design_rate / 60.
    const designCostCents = laborCentsFromMinutes(input.designMinutes, input.designRateCentsPerHour);
    // Print cost: passthrough + its own markup (same markupBp).
    const printCostCents = applyMarkup(input.printCostCents, input.markupBp);
    const correctionFeeCents = input.correctionFeeCents;
    const fixedAddOnCents = input.fixedAddOnCents;
    // -- §29.4 rule 5: line subtotal = labor + material + design + print + fixed adds
    const entries = [
        { label: 'labor', minutes: laborMinutes, cents: laborCostCents },
        { label: 'material', cents: materialCostCents },
        { label: 'design', minutes: input.designMinutes, cents: designCostCents },
        { label: 'print', cents: printCostCents },
        { label: 'correction_fee', cents: correctionFeeCents },
        { label: 'fixed_add_on', cents: fixedAddOnCents },
    ];
    const lineSubtotalCents = entries.reduce((acc, e) => acc + e.cents, 0);
    const breakdown = { entries };
    return finalizeResult({
        labor: [{ label: 'labor', minutes: laborMinutes, cents: laborCostCents }],
        estimatedMinutes: laborMinutes,
        filmFtX10,
        materialCostCents,
        laborCostCents,
        designCostCents,
        printCostCents,
        lineSubtotalCents,
        breakdown,
    }, 'wrap');
}
// ===========================================================================
// PPF — zone-driven sqft footage, no waste (product decision: PPF material
// cost is never waste-inflated, unlike wrap film footage). Result includes
// cure_hold_minutes.
// ===========================================================================
export function pricePpfLine(input, profile) {
    const laborMinutes = input.zoneMinutes;
    // Material: sqft_x10 × cost_per_sqft_cents / 10, then markup. No waste
    // applied anywhere - mirrors PpfTab.tsx's computeZonePriceCents() exactly.
    const materialBeforeMarkup = roundHalfUp((input.zoneSqftX10 * input.costPerSqftCents) / 10);
    const materialCostCents = applyMarkup(materialBeforeMarkup, profile.markupBp);
    const laborCostCents = laborCentsFromMinutes(laborMinutes, profile.laborRateCentsPerHour);
    const designCostCents = laborCentsFromMinutes(input.designMinutes, profile.designRateCentsPerHour);
    const fixedAddOnCents = input.fixedAddOnCents;
    const entries = [
        { label: 'labor', minutes: laborMinutes, cents: laborCostCents },
        { label: 'material', cents: materialCostCents },
        { label: 'design', minutes: input.designMinutes, cents: designCostCents },
        { label: 'fixed_add_on', cents: fixedAddOnCents },
    ];
    const lineSubtotalCents = entries.reduce((acc, e) => acc + e.cents, 0);
    return finalizeResult({
        labor: [{ label: 'labor', minutes: laborMinutes, cents: laborCostCents }],
        estimatedMinutes: laborMinutes,
        filmFtX10: input.zoneSqftX10,
        materialCostCents,
        laborCostCents,
        designCostCents,
        printCostCents: 0,
        lineSubtotalCents,
        breakdown: { entries, cureHoldMinutes: input.cureHoldMinutes },
        cureHoldMinutes: input.cureHoldMinutes,
    }, 'ppf');
}
// ===========================================================================
// TINT — flat per-zone price from tint_price_matrix; already final, no markup.
// ===========================================================================
export function priceTintLine(input, profile) {
    void input.vlt; // recorded passthrough only.
    const designCostCents = laborCentsFromMinutes(input.designMinutes, profile.designRateCentsPerHour);
    const fixedAddOnCents = input.fixedAddOnCents;
    const entries = [
        { label: 'price', cents: input.priceCents },
        { label: 'design', minutes: input.designMinutes, cents: designCostCents },
        { label: 'fixed_add_on', cents: fixedAddOnCents },
    ];
    const lineSubtotalCents = entries.reduce((acc, e) => acc + e.cents, 0);
    return finalizeResult({
        labor: [],
        estimatedMinutes: input.zoneMinutes,
        filmFtX10: 0,
        materialCostCents: input.priceCents,
        laborCostCents: 0,
        designCostCents,
        printCostCents: 0,
        lineSubtotalCents,
        breakdown: { entries },
    }, 'tint');
}
// ===========================================================================
// CERAMIC — flat price from ceramic_package_tiers; already final, no markup.
// Result includes cure_hold_minutes.
// ===========================================================================
export function priceCeramicLine(input, profile) {
    void input.layers; // recorded passthrough only.
    void input.warrantyYears; // recorded passthrough only.
    const designCostCents = laborCentsFromMinutes(input.designMinutes, profile.designRateCentsPerHour);
    const fixedAddOnCents = input.fixedAddOnCents;
    const entries = [
        { label: 'price', cents: input.priceCents },
        { label: 'design', minutes: input.designMinutes, cents: designCostCents },
        { label: 'fixed_add_on', cents: fixedAddOnCents },
    ];
    const lineSubtotalCents = entries.reduce((acc, e) => acc + e.cents, 0);
    return finalizeResult({
        labor: [],
        estimatedMinutes: input.packageMinutes,
        filmFtX10: 0,
        materialCostCents: input.priceCents,
        laborCostCents: 0,
        designCostCents,
        printCostCents: 0,
        lineSubtotalCents,
        breakdown: { entries, cureHoldMinutes: input.cureHoldMinutes },
        cureHoldMinutes: input.cureHoldMinutes,
    }, 'ceramic');
}
// ===========================================================================
// QUOTE-LEVEL — §29.4 rule 6: rush → mobile → exotic → fleet → floor.
// ===========================================================================
export function priceQuoteLevel(input) {
    const subtotal = input.lineSubtotals.reduce((acc, c) => acc + c, 0);
    // 1. rush surcharge (bp of subtotal)
    const appliedRushCents = roundHalfUp((subtotal * input.rushBp) / 10000);
    // 2. mobile install fee (flat)
    const appliedMobileCents = input.mobileInstallFeeCents;
    // 3. exotic liability (bp of subtotal) if applicable
    const appliedExoticCents = input.exoticApplies
        ? roundHalfUp((subtotal * input.exoticLiabilityBp) / 10000)
        : 0;
    // 4. fleet discount (negative) if count >= threshold
    const appliedFleetCents = input.fleetVehicleCount >= input.fleetDiscountThreshold
        ? -roundHalfUp((subtotal * input.fleetDiscountBp) / 10000)
        : 0;
    const subtotalBeforeFloorCents = subtotal + appliedRushCents + appliedMobileCents + appliedExoticCents + appliedFleetCents;
    // 5. floor
    const floorApplied = subtotalBeforeFloorCents < input.minJobPriceCents;
    const flooredCents = floorApplied ? input.minJobPriceCents : subtotalBeforeFloorCents;
    const entries = [
        { label: 'lines_subtotal', cents: subtotal },
        { label: 'rush', cents: appliedRushCents },
        { label: 'mobile_install', cents: appliedMobileCents },
        { label: 'exotic_liability', cents: appliedExoticCents },
        { label: 'fleet_discount', cents: appliedFleetCents },
    ];
    if (floorApplied) {
        entries.push({ label: 'min_job_floor_adjustment', cents: flooredCents - subtotalBeforeFloorCents });
    }
    // Invariants: subtotal-before-floor sums from its components; final total sums
    // exactly from all entries; the total is never below the floor.
    const beforeFloorSum = subtotal + appliedRushCents + appliedMobileCents + appliedExoticCents + appliedFleetCents;
    if (beforeFloorSum !== subtotalBeforeFloorCents) {
        throw new AppError('VALIDATION', `pricing invariant: quote-level before-floor sum ${beforeFloorSum} !== ${subtotalBeforeFloorCents}`, {
            userMessage: 'Something went wrong pricing this line. Please contact support.',
        });
    }
    assertBreakdownSums(entries, flooredCents, 'quote-level');
    if (flooredCents < input.minJobPriceCents) {
        throw new AppError('VALIDATION', `pricing invariant: quote total ${flooredCents} below floor ${input.minJobPriceCents} without flooring`, {
            userMessage: 'Something went wrong pricing this line. Please contact support.',
        });
    }
    assertNonNegInt(flooredCents, 'quote-level flooredCents');
    return {
        subtotalBeforeFloorCents,
        flooredCents,
        appliedRushCents,
        appliedMobileCents,
        appliedExoticCents,
        appliedFleetCents,
        breakdown: { entries, floorApplied },
    };
}
// ===========================================================================
// ADD-ON SERVICE LINE (§36 D-36-04) — flat or hourly. `minutes` bills (hourly)
// and always flows into estimatedMinutes so §30 scheduling consumes it.
// ===========================================================================
export function priceAddonLine(input) {
    assertNonNegInt(input.rateCents, 'addon rateCents');
    assertNonNegInt(input.minutes, 'addon minutes');
    const cents = input.pricingType === 'flat'
        ? input.rateCents
        : laborCentsFromMinutes(input.minutes, input.rateCents);
    assertNonNegInt(cents, 'addon cents');
    return { cents, estimatedMinutes: input.minutes };
}
// ===========================================================================
// PER-CATEGORY MINIMUMS (§36 D-36-05) — visible, monotonic floor per service
// category. Returns only the categories that were actually raised; each
// adjustment is strictly positive (INV-36-MIN-MONOTONIC). A waived or disabled
// (minCents 0) category never produces an adjustment, so with everything
// dormant this is a no-op and quote totals are byte-identical to pre-§36.
// ===========================================================================
export function applyCategoryMinimums(inputs) {
    const adjustments = [];
    for (const item of inputs) {
        assertNonNegInt(item.subtotalCents, `category "${item.category}" subtotalCents`);
        assertNonNegInt(item.minCents, `category "${item.category}" minCents`);
        if (item.waived)
            continue;
        if (item.minCents > item.subtotalCents) {
            const adjustmentCents = item.minCents - item.subtotalCents;
            assertNonNegInt(adjustmentCents, `category "${item.category}" adjustmentCents`);
            adjustments.push({ category: item.category, adjustmentCents });
        }
    }
    return adjustments;
}
// ===========================================================================
// Dispatcher — §29.4: priceServiceLine(input, profile) → PricingResult.
// ===========================================================================
export function priceServiceLine(input, profile) {
    switch (input.serviceKind) {
        case 'wrap':
            // Wrap folds profile rates into its input at construction time; the caller
            // is responsible for wiring profile → WrapPricingInput. We still validate
            // the profile is present for signature parity.
            void profile;
            return priceWrapLine(input.input);
        case 'ppf':
            return pricePpfLine(input.input, profile);
        case 'tint':
            return priceTintLine(input.input, profile);
        case 'ceramic':
            return priceCeramicLine(input.input, profile);
        case 'print':
            // Print folds profile rates into its input the same way wrap does; the
            // caller is responsible for wiring profile -> PrintPricingInput.
            // pricePrintLine lives in its own module (no access to the private
            // finalizeResult below), so the §29.8 invariant guards run here instead.
            void profile;
            return finalizeResult(pricePrintLine(input.input), 'print');
    }
}
// ---------------------------------------------------------------------------
// Finalizer — runs the §29.8 invariant guards on every line result.
// ---------------------------------------------------------------------------
function finalizeResult(result, context) {
    assertNonNegInt(result.estimatedMinutes, `${context} estimatedMinutes`);
    assertNonNegInt(result.filmFtX10, `${context} filmFtX10`);
    assertNonNegInt(result.materialCostCents, `${context} materialCostCents`);
    assertNonNegInt(result.laborCostCents, `${context} laborCostCents`);
    assertNonNegInt(result.designCostCents, `${context} designCostCents`);
    assertNonNegInt(result.printCostCents, `${context} printCostCents`);
    assertNonNegInt(result.lineSubtotalCents, `${context} lineSubtotalCents`);
    for (const item of result.breakdown.entries) {
        assertNonNegInt(item.cents, `${context} breakdown "${item.label}" cents`);
        if (item.minutes !== undefined)
            assertNonNegInt(item.minutes, `${context} breakdown "${item.label}" minutes`);
    }
    assertBreakdownSums(result.breakdown.entries, result.lineSubtotalCents, context);
    return result;
}
//# sourceMappingURL=engine.js.map